import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnChanges,
	Output,
	SimpleChanges,
	ViewChild,
} from '@angular/core';
import { isEqual } from 'lodash';
import { BehaviorSubject, Subject, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BackpackModule } from '../../backpack/backpack.module';
import {
	ResultsGroup,
	SearchResultsItem,
	SelectedItem,
	StudentSearchResultsComponent,
} from '../../backpack/student-search-results/student-search-results.component';
import { User } from '../../models';
import { StudentList } from '../../models/StudentList';

@Component({
	selector: 'sp-student-group-select',
	standalone: true,
	imports: [CommonModule, OverlayModule, BackpackModule, StudentSearchResultsComponent],
	template: `
		<div #listWrapper class="tw-rounded-xl tw-border-b tw-border-solid tw-border-gray-200 tw-bg-white tw-w-[484px] tw-box-border tw-p-2">
			<bp-student-search-results
				[itemGroups]="(itemGroups$ | async) || []"
				(uploadStudentListClicked)="showImportStudentList.emit()"
				(itemSelected)="itemSelected($event)"></bp-student-search-results>
		</div>
	`,
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StudentGroupSelectComponent implements OnChanges, AfterViewInit {
	@ViewChild('listWrapper', { static: true }) listWrapper!: ElementRef;
	@Input() groups: StudentList[] = [];
	@Input() students: User[] = [];
	@Input() selectedIds: { groupIds: number[]; studentIds: number[] } = { groupIds: [], studentIds: [] };
	@Input() selectableIds: { groupIds: number[]; studentIds: number[] } = { groupIds: [], studentIds: [] };
	@Input() flexPeriodName = '';
	@Output() handleSelect: EventEmitter<StudentList | User> = new EventEmitter<StudentList | User>();
	@Output() showImportStudentList: EventEmitter<void> = new EventEmitter<void>();

	itemGroups$: BehaviorSubject<ResultsGroup[]> = new BehaviorSubject<ResultsGroup[]>([]);
	stopTimer$: Subject<void> = new Subject<void>();

	ngOnChanges(changes: SimpleChanges): void {
		let groupItem: ResultsGroup;
		let studentItem: ResultsGroup;
		let currentValue = this.itemGroups$.getValue();
		const groupsHaveChanged = !isEqual(changes.groups?.previousValue, changes.groups?.currentValue);
		const studentsHaveChanged = !isEqual(changes.students?.previousValue, changes.students?.currentValue);
		const selectedIdsHaveChanged = !isEqual(changes.alreadySelectedIds?.previousValue?.groupIds, changes.alreadySelectedIds?.currentValue?.groupIds);
		let itemsUpdated = false;

		if (groupsHaveChanged || selectedIdsHaveChanged) {
			const items: SearchResultsItem[] = this.groups.map((group) => {
				const isSelectable = this.isGroupSelectable(group);
				return {
					name: group.title,
					isSelectable: isSelectable,
					isEditable: false,
					id: group.id,
					img: 'assets/Groups Icon.svg',
					details: isSelectable ? `${group.users.length} ${group.users.length === 1 ? 'student' : 'students'}` : undefined,
					disabledCheckmark: false,
					disabledMessage: !isSelectable ? this.getUnselectableGroupText(group) : undefined,
				};
			});
			if (items.length === 0) {
				// If there are no groups, we should clear the group item if it exists
				const idx = currentValue.findIndex((g) => g.key === 'group');
				if (idx !== -1) {
					currentValue = currentValue.filter((g) => g.key !== 'group');
					itemsUpdated = true;
				}
			}
			if (items.length > 0) {
				groupItem = {
					key: 'group',
					items: items,
				};
				const idx = currentValue.findIndex((g) => g.key === 'group');
				if (idx !== -1) {
					currentValue = [...currentValue.slice(0, idx), groupItem, ...currentValue.slice(idx + 1)];
				} else {
					currentValue = [...currentValue, groupItem];
				}
				itemsUpdated = true;
			}
		}
		if (studentsHaveChanged || selectedIdsHaveChanged) {
			const items: SearchResultsItem[] = this.students.map((student) => ({
				name: student.display_name,
				isSelectable: this.isStudentSelectable(student.id),
				isEditable: false,
				id: student.id,
				img: student.profile_picture || 'assets/Avatar Default.svg',
				disabledCheckmark: !this.isStudentSelectable(student.id),
				disabledMessage: !this.isStudentSelectable(student.id) ? this.getUnselectableStudentText(student.id) : undefined,
			}));

			if (items.length === 0) {
				// If there are no groups, we should clear the group item if it exists
				const idx = currentValue.findIndex((g) => g.key === 'student');
				if (idx !== -1) {
					currentValue = currentValue.filter((g) => g.key !== 'student');
					itemsUpdated = true;
				}
			}
			if (items.length > 0) {
				studentItem = {
					key: 'student',
					items: items,
				};

				const idx = currentValue.findIndex((g) => g.key === 'student');
				if (idx !== -1) {
					currentValue = [...currentValue.slice(0, idx), studentItem, ...currentValue.slice(idx + 1)];
				} else {
					currentValue = [...currentValue, studentItem];
				}
				itemsUpdated = true;
			}
		}
		if (itemsUpdated) {
			this.itemGroups$.next(currentValue);
		}
	}

	ngAfterViewInit(): void {
		// Calculate the height of the listWrapper to ensure it stays 20px above the bottom of the window.
		// It uses cdkConnectedOverlay to position the listWrapper initially, which isn't always done when this
		// lifecycle hook is run, so we need to use a timer to check the position of the listWrapper.
		timer(0, 1000)
			.pipe(takeUntil(this.stopTimer$))
			.subscribe(() => {
				this.updateListWrapperPosition();
			});
	}

	updateListWrapperPosition(): void {
		const windowHeight = window.innerHeight;
		const listWrapperTop = this.listWrapper.nativeElement.getBoundingClientRect().top;
		if (listWrapperTop > 0) {
			this.listWrapper.nativeElement.style.top = `${windowHeight - this.listWrapper.nativeElement.offsetHeight - 20}px`;
			const maxHeight = windowHeight - listWrapperTop - 20; // 20px above the bottom
			this.listWrapper.nativeElement.style.height = `${maxHeight}px`;
			this.listWrapper.nativeElement.style.overflowY = 'auto'; // Ensure scrollable if content exceeds maxHeight
			this.stopTimer$.next();
			this.stopTimer$.complete();
		}
	}

	private isGroupSelectable(group: StudentList): boolean {
		if (this.selectedIds.groupIds.length > 0) {
			if (this.selectedIds.groupIds.includes(group.id)) {
				return false;
			}
		}
		if (this.selectableIds.groupIds.length > 0) {
			// if the group has users that are not selectable, then the group is not selectable
			if (this.areSomeUsersInGroupUnselectable(group)) {
				return false;
			}
		}

		return true;
	}

	private getUnselectableGroupText(group: StudentList): string {
		if (this.selectedIds.groupIds.length > 0) {
			if (this.selectedIds.groupIds.includes(group.id)) {
				// if the group is already selected, then it is not selectable
				return 'Already selected';
			}
		}
		if (this.selectableIds.groupIds.length > 0) {
			// the group has users that are not selectable
			if (this.areSomeUsersInGroupUnselectable(group)) {
				return `No visibility to ${this.flexPeriodName}`;
			}
		}
		return '';
	}

	private areSomeUsersInGroupUnselectable(group: StudentList): boolean {
		return group.users.some((user) => !this.selectableIds.studentIds.includes(user.id));
	}

	private isStudentSelectable(id: number): boolean {
		const isAlreadySelected = this.selectedIds.studentIds.length > 0 && this.selectedIds.studentIds.includes(id);
		const isNotInSelectableIds = this.selectableIds.studentIds.length > 0 && !this.selectableIds.studentIds.includes(id);
		return !isAlreadySelected && !isNotInSelectableIds;
	}

	private getUnselectableStudentText(id: number): string {
		let text = '';
		if (this.selectedIds.studentIds.length > 0 && this.selectedIds.studentIds.includes(id)) {
			text = 'Already selected';
		} else if (this.selectableIds.studentIds.length > 0 && !this.selectableIds.studentIds.includes(id)) {
			text = `No visibility to ${this.flexPeriodName}`;
		}
		return text;
	}

	itemSelected(selected: SelectedItem): void {
		switch (selected.resultKey) {
			case 'group': {
				const group = this.groups.find((g) => g.id === selected.item.id);
				if (group) {
					this.selectGroup(group);
				}
				break;
			}
			case 'student': {
				const student = this.students.find((s) => s.id === selected.item.id);
				if (student) {
					this.selectStudent(student);
				}
				break;
			}
			default: {
				console.warn('Unknown item type selected:', selected.resultKey, selected.item);
				break;
			}
		}
	}

	private selectGroup(item: StudentList): void {
		if (!this.isGroupSelectable(item)) {
			return;
		}
		this.handleSelect.emit(item);
	}

	private selectStudent(student: User): void {
		if (!this.isStudentSelectable(student.id)) {
			return;
		}
		this.handleSelect.emit(student);
	}
}
